import React, { useEffect, useState, Suspense, lazy } from "react";
import { Element, Events, scrollSpy } from "react-scroll";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { ProjectProvider } from "context/ProjectContext";

import Header from "Pages/Header";

// Lazy loading components for better performance
const WebApp = lazy(() => import("Componets/WebApp"));
const MobileApp = lazy(() => import("Componets/MobileApp"));
const WebSites = lazy(() => import("Componets/WebSites"));
const About = lazy(() => import("Pages/About"));
const ParticleBackground = lazy(() => import("Componets/ParticleBackground"));

// Subtle loading indicator
const SectionLoader = () => (
  <div className="w-full flex justify-center py-12">
    <div className="w-10 h-10 border-4 border-teal-500/30 border-t-teal-500 rounded-full animate-spin"></div>
  </div>
);

const FadeInWhenVisible: React.FC<{ children: React.ReactNode }> = ({ children }) => {

  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="w-full relative"
    >
      {children}
    </motion.div>
  );
};

export default function QuickView() {
  const [activeTab, setActiveTab] = useState("webapps");

  useEffect(() => {
    Events.scrollEvent.register('begin', () => { });
    Events.scrollEvent.register('end', () => { });
    scrollSpy.update();

    // Listen for custom event from SideNav
    const handleTabSwitch = (e: any) => {
      if (e.detail && ["webapps", "websites", "apps"].includes(e.detail)) {
        setActiveTab(e.detail);
      }
    };
    window.addEventListener('switchTab', handleTabSwitch);

    return () => {
      Events.scrollEvent.remove('begin');
      Events.scrollEvent.remove('end');
      window.removeEventListener('switchTab', handleTabSwitch);
    };
  }, []);

  const categories = [
    { id: "webapps", label: "WebApps", Component: WebApp },
    { id: "apps", label: "Apps", Component: MobileApp }, 
    { id: "websites", label: "Web Sites", Component: WebSites },
  ];

  const ActiveComponent = categories.find((c) => c.id === activeTab)?.Component;

  return (
    <ProjectProvider>
    <div className="bg-white text-gray-900 min-h-screen relative">
      <Header />

      <div className="container mx-auto px-4 md:px-6 pt-8 flex flex-col items-center relative">
        <div className="w-full max-w-6xl flex flex-col">
          <Element name="projects-section" className="mb-8">
            <FadeInWhenVisible>
              {/* Tab Navigation */}
              <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-6 md:mb-10">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveTab(cat.id)}
                    className={`px-4 py-2 text-sm md:px-6 md:py-3 md:text-base rounded-full font-bold transition-all shadow-sm ${
                      activeTab === cat.id
                        ? "bg-teal-500 text-white shadow-teal-500/25 scale-105"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </FadeInWhenVisible>

            {/* Tab Content */}
            <Element name="project-detail" className="min-h-[300px]">
              <Suspense fallback={<SectionLoader />}>
                <AnimatePresence exitBeforeEnter>
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3 }}
                  >
                    {ActiveComponent && <ActiveComponent />}
                  </motion.div>
                </AnimatePresence>
              </Suspense>
            </Element>
            
            {/* Hidden elements for react-scroll backward compatibility if needed, though we use switchTab now */}
            {categories.map(cat => <Element key={cat.id} name={cat.id} className="hidden" />)}
          </Element>

          <Suspense fallback={<SectionLoader />}>
            {/* About Section */}
            <Element name="about" className="mt-12">
              <FadeInWhenVisible>
                <div className="mb-12">
                  <h2 className="text-3xl lg:text-5xl font-extrabold text-gray-900 mb-4 flex items-center gap-4">
                    About Me
                  </h2>
                  <div className="w-20 h-1.5 bg-teal-500 rounded-full shadow-sm"></div>
                </div>
                <About />
              </FadeInWhenVisible>
            </Element>
          </Suspense>
        </div>
      </div>
      
      <Suspense fallback={null}>
        <ParticleBackground />
      </Suspense>
    </div>
    </ProjectProvider>
  );
}

